import React, { useState, useRef } from "react";
import { createPortal } from "react-dom";
import { Shield, Eye, Flame, Activity, Zap, Layers, Cpu, Maximize2, Minimize2, X, ZoomIn, ZoomOut, RotateCcw, Move, Radio, Lock } from "lucide-react";
import { motion, AnimatePresence } from "motion/react";

const pleiadesClansArchetypesImg = new URL("../assets/images/Pleiades star cluster - harmonic frequency designation.webp", import.meta.url).href;

interface ArchetypeSelectorProps {
  language: "en" | "hr";
}

interface ArchetypeItem {
  id: string;
  name: { en: string; hr: string };
  title: { en: string; hr: string };
  desc: { en: string; hr: string };
  attribute: { en: string; hr: string };
  color: string;
  borderColor: string;
  textColor: string;
  icon: any;
}

export default function ArchetypeSelector({ language }: ArchetypeSelectorProps) {
  const [activeArchetype, setActiveArchetype] = useState<string>("unity");
  const [isImageModalOpen, setIsImageModalOpen] = useState<boolean>(false);
  const [scanEffectActive, setScanEffectActive] = useState<boolean>(true);

  // High-Res Zoom & Pan state
  const [zoomScale, setZoomScale] = useState<number>(1);
  const [origin, setOrigin] = useState<{ x: number; y: number }>({ x: 50, y: 50 });
  const [panOffset, setPanOffset] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const [dragStart, setDragStart] = useState<{ x: number; y: number }>({ x: 0, y: 0 });

  const containerRef = useRef<HTMLDivElement>(null);

  const resetZoom = () => {
    setZoomScale(1);
    setOrigin({ x: 50, y: 50 });
    setPanOffset({ x: 0, y: 0 });
  };

  // Close modal and exit fullscreen
  const closeModal = () => {
    setIsImageModalOpen(false);
    if (typeof document !== "undefined" && document.fullscreenElement) {
      document.exitFullscreen().catch(() => {});
    }
  };

  const openModal = () => {
    resetZoom();
    setIsImageModalOpen(true);
    if (typeof document !== "undefined" && document.documentElement.requestFullscreen) {
      document.documentElement.requestFullscreen().catch(() => {});
    }
  };

  // Close modal on Escape key press or browser native fullscreen exit
  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isImageModalOpen) {
        closeModal();
      }
    };
    const handleFullscreenChange = () => {
      if (typeof document !== "undefined" && !document.fullscreenElement && isImageModalOpen) {
        setIsImageModalOpen(false);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    if (typeof document !== "undefined") {
      document.addEventListener("fullscreenchange", handleFullscreenChange);
    }
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      if (typeof document !== "undefined") {
        document.removeEventListener("fullscreenchange", handleFullscreenChange);
      }
    };
  }, [isImageModalOpen]);

  const handleZoomIn = () => {
    setZoomScale((prev) => Math.min(4, Number((prev + 0.5).toFixed(1))));
  };

  const handleZoomOut = () => {
    setZoomScale((prev) => {
      const next = Math.max(1, Number((prev - 0.5).toFixed(1)));
      if (next === 1) resetZoom();
      return next;
    });
  };

  const handleImageClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (zoomScale === 1) {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        const x = ((e.clientX - rect.left) / rect.width) * 100;
        const y = ((e.clientY - rect.top) / rect.height) * 100;
        setOrigin({ x: Math.max(0, Math.min(100, x)), y: Math.max(0, Math.min(100, y)) });
      }
      setZoomScale(2.5);
    } else if (!isDragging) {
      resetZoom();
    }
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (isDragging) {
      const dx = e.clientX - dragStart.x;
      const dy = e.clientY - dragStart.y;
      setPanOffset((prev) => ({ x: prev.x + dx, y: prev.y + dy }));
      setDragStart({ x: e.clientX, y: e.clientY });
    } else if (zoomScale > 1 && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width) * 100;
      const y = ((e.clientY - rect.top) / rect.height) * 100;
      setOrigin({ x: Math.max(0, Math.min(100, x)), y: Math.max(0, Math.min(100, y)) });
    }
  };

  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if (zoomScale > 1) {
      setIsDragging(true);
      setDragStart({ x: e.clientX, y: e.clientY });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleWheel = (e: React.WheelEvent<HTMLDivElement>) => {
    e.preventDefault();
    const delta = e.deltaY < 0 ? 0.3 : -0.3;
    setZoomScale((prev) => {
      const next = Math.min(4, Math.max(1, Number((prev + delta).toFixed(1))));
      if (next === 1) resetZoom();
      return next;
    });
  };

  const archetypes: ArchetypeItem[] = [
    {
      id: "unity",
      name: { en: "1. Unity (Alcyone)", hr: "1. Jedinstvo (Alcyone)" },
      title: { en: "The Sacred Pillar of Unified Divine Intent", hr: "Sveti Stup Ujedinjene Božanske Namjere" },
      desc: {
        en: "Focuses on the synthesis of individual experience into a unified galactic mind. This clan manages cosmic gravity, planetary telepathy networks, and high-level coordinate governance.",
        hr: "Fokusira se na sintezu individualnog iskustva u ujedinjeni galaktički um. Ovaj klan upravlja kozmičkom gravitacijom, mrežama planetarne telepatije i koordiniranim upravljanjem na visokoj razini."
      },
      attribute: { en: "Telepathy & Synthesis", hr: "Telepatija i Sinteza" },
      color: "bg-blue-500/10",
      borderColor: "border-blue-500/40 hover:border-blue-400",
      textColor: "text-blue-400",
      icon: Shield
    },
    {
      id: "bioresonance",
      name: { en: "2. Bioresonance (Maia)", hr: "2. Biorezonancija (Maia)" },
      title: { en: "The Genetic Caretakers of Stellar Living Ecosystems", hr: "Genetski Skrbnici Zvjezdanih Živih Ekosustava" },
      desc: {
        en: "Preserves the archetypal blueprints for flora, fauna, and physical humanoid vessels. They specialize in organic design, planetary seeding, and the geometric templates of nature.",
        hr: "Čuva arhetipske nacrte za floru, faunu i fizičke humanoidne posude. Specijalizirani su za organski dizajn, planetarno sijanje i geometrijske predloške prirode."
      },
      attribute: { en: "Organic Sacred Geometry", hr: "Organska Sveta Geometrija" },
      color: "bg-emerald-500/10",
      borderColor: "border-emerald-500/40 hover:border-emerald-400",
      textColor: "text-emerald-400",
      icon: Activity
    },
    {
      id: "alchemy",
      name: { en: "3. Inner Alchemy (Merope)", hr: "3. Unutarnja Alkemija (Merope)" },
      title: { en: "The Transmuters of Multi-Dimensional Trauma & Shadows", hr: "Transmutatori Višedimenzionalnih Trauma i Sjena" },
      desc: {
        en: "Provides spiritual therapy and shadow integration. This clan helps civilizations process the karmic residue of war, separation, and material densities, restoring emotional balance.",
        hr: "Pruža duhovnu terapiju i integraciju sjene. Ovaj klan pomaže civilizacijama da procesuiraju karmičke ostatke rata, odvojenosti i materijalnih gustoća, obnavljajući emocionalnu ravnotežu."
      },
      attribute: { en: "Shadow Integration & Resurrection", hr: "Integracija Sjene i Uskrsnuće" },
      color: "bg-purple-500/10",
      borderColor: "border-purple-500/40 hover:border-purple-400",
      textColor: "text-purple-400",
      icon: Flame
    },
    {
      id: "sonic",
      name: { en: "4. Sonic Architecture (Taygeta)", hr: "4. Zvučna Arhitektura (Taygeta)" },
      title: { en: "The Harmonizers of Spatial Fields through Sound Waveforms", hr: "Harmonizatori Prostornih Polja Kroz Zvučne Valove" },
      desc: {
        en: "Studies vibrational physics, frequencies, and the language of light. They construct buildings and navigate spatial folds by emitting pure tonal geometries that interact directly with the aether.",
        hr: "Proučava vibracijsku fiziku, frekvencije i jezik svjetlosti. Grade građevine i navigiraju prostornim naborima emitirajući čiste tonske geometrije koje izravno djeluju s eterom."
      },
      attribute: { en: "Vibrational Matter Engineering", hr: "Inženjerstvo Vibracijske Materije" },
      color: "bg-cyan-500/10",
      borderColor: "border-cyan-500/40 hover:border-cyan-400",
      textColor: "text-cyan-400",
      icon: Layers
    },
    {
      id: "quantum",
      name: { en: "5. Quantum Grid (Electra)", hr: "5. Kvantna Mreža (Electra)" },
      title: { en: "The Engineers of High-Capacity Interstellar Energy Nodes", hr: "Inženjeri Visokokapacitetnih Međuzvjezdanih Energetskih Čvorova" },
      desc: {
        en: "Designs zero-point energy networks and cosmic propulsion tech. They specialize in scalar fields, torsion dynamics, and the interface connecting biological consciousness with material computers.",
        hr: "Dizajnira energetske mreže nulte točke i kozmičku pogonsku tehnologiju. Specijalizirani su za skalarna polja, torzijsku dinamiku i sučelje koje povezuje biološku svijest s materijalnim računalima."
      },
      attribute: { en: "Zero-Point Scalar Physics", hr: "Skalarna Fizika Nulte Točke" },
      color: "bg-amber-500/10",
      borderColor: "border-amber-500/40 hover:border-amber-400",
      textColor: "text-amber-400",
      icon: Cpu
    },
    {
      id: "oracle",
      name: { en: "6. Oracle Insight (Celaeno)", hr: "6. Vizija Proročišta (Celaeno)" },
      title: { en: "The Custodians of Akashic Timelines & Future Pathways", hr: "Čuvari Akaških Vremenskih Linija i Budućih Puteva" },
      desc: {
        en: "Operates as the galactic library, mapping multi-dimensional history and potential evolutionary timelines. They assist younger civilizations in choosing timelines of peace and expansion.",
        hr: "Djeluje kao galaktička knjižnica, mapirajući višedimenzionalnu povijest i potencijalne evolucijske vremenske linije. Pomažu mlađim civilizacijama da odaberu vremenske linije mira i ekspanzije."
      },
      attribute: { en: "Akashic Navigation & Destiny", hr: "Akaška Navigacija i Sudbina" },
      color: "bg-indigo-500/10",
      borderColor: "border-indigo-500/40 hover:border-indigo-400",
      textColor: "text-indigo-400",
      icon: Eye
    },
    {
      id: "structure",
      name: { en: "7. Structural Anchor (Sterope)", hr: "7. Strukturno Sidro (Sterope)" },
      title: { en: "The Stabilizers of High-Density Light in Physical Reality", hr: "Stabilizatori Svjetlosti Visoke Gustoće u Fizičkoj Stvarnosti" },
      desc: {
        en: "Bridges high-vibrational thoughts with solid physical layers. They represent the grounding force that ensures spiritual insights are translated into functional, sustainable material systems.",
        hr: "Premošćuje visokovibracijske misli s čvrstim fizičkim slojevima. Oni predstavljaju silu uzemljenja koja osigurava da se duhovni uvidi prevedu u funkcionalne, održive materijalne sustave."
      },
      attribute: { en: "Physical-Etheric Grounding", hr: "Fizičko-Eterično Uzemljenje" },
      color: "bg-rose-500/10",
      borderColor: "border-rose-500/40 hover:border-rose-400",
      textColor: "text-rose-400",
      icon: Zap
    }
  ];

  const currentArchetype = archetypes.find((a) => a.id === activeArchetype) || archetypes[0];

  return (
    <div className="bg-cosmic-black/60 border border-cosmic-indigo/80 rounded-2xl p-6 box-glow backdrop-blur-md" id="archetypes-section">
      <div className="flex items-center gap-2 mb-4">
        <Zap className="w-5 h-5 text-golden-resonance animate-pulse" />
        <h3 className="text-lg font-display font-semibold tracking-wide text-ethereal-white">
          {language === "en" ? "Pleiadian Clans — Seven Star Archetypes" : "Plejadanski Klanovi — Sedam Zvjezdanih Arhetipova"}
        </h3>
      </div>

      <p className="text-xs text-gray-400 mb-6 font-sans max-w-2xl">
        {language === "en"
          ? "The Pleiadian civilization splits its collective focus into seven distinct schools or paths of consciousness. Select a clan below to explore their specific multi-dimensional roles."
          : "Plejadanska civilizacija dijeli svoj kolektivni fokus na sedam različitih škola ili puteva svijesti. Odaberite klan u nastavku kako biste istražili njihove specifične višedimenzionalne uloge."}
      </p>

      <div className="grid grid-cols-1 md:grid-cols-12 gap-6 items-start">
        {/* Buttons List */}
        <div className="md:col-span-5 space-y-2">
          {archetypes.map((arch) => {
            const isSelected = arch.id === activeArchetype;
            const IconComp = arch.icon;
            return (
              <button
                key={arch.id}
                onClick={() => setActiveArchetype(arch.id)}
                className={`w-full p-3.5 rounded-xl border text-left transition-all duration-300 flex items-center justify-between cursor-pointer group ${
                  isSelected
                    ? `${arch.color} ${arch.borderColor} shadow-[0_0_12px_rgba(74,144,226,0.1)]`
                    : "bg-cosmic-black/25 border-cosmic-indigo/30 hover:bg-cosmic-indigo/20 text-gray-400 hover:text-white"
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className={`p-1.5 rounded-lg bg-cosmic-black/60 border border-cosmic-indigo/40 group-hover:border-quantum-turquoise/40 transition-colors ${isSelected ? arch.textColor : "text-gray-500"}`}>
                    <IconComp className="w-4 h-4" />
                  </div>
                  <span className="text-xs font-mono font-medium tracking-wide">
                    {arch.name[language]}
                  </span>
                </div>
                <div className={`w-1.5 h-1.5 rounded-full transition-transform group-hover:scale-125 ${isSelected ? "bg-quantum-turquoise shadow-[0_0_8px_var(--quantum-turquoise)]" : "bg-gray-700"}`}></div>
              </button>
            );
          })}
        </div>

        {/* Dynamic Detail Card */}
        <div className="md:col-span-7 bg-cosmic-black/40 border border-cosmic-indigo/40 rounded-xl p-6 min-h-[300px] flex flex-col justify-between">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentArchetype.id}
              initial={{ opacity: 0, x: 15 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -15 }}
              transition={{ duration: 0.25 }}
              className="space-y-4"
            >
              <div className="flex items-center justify-between pb-3 border-b border-cosmic-indigo/30">
                <span className={`text-[10px] font-mono uppercase tracking-wider ${currentArchetype.textColor}`}>
                  {currentArchetype.attribute[language]}
                </span>
                <span className="text-[9px] font-mono text-gray-600">CLUSTER GRID v1.08</span>
              </div>

              <h4 className="text-lg font-display font-semibold text-ethereal-white leading-tight">
                {currentArchetype.title[language]}
              </h4>

              <p className="text-sm text-gray-300 leading-relaxed font-sans">
                {currentArchetype.desc[language]}
              </p>
            </motion.div>
          </AnimatePresence>

          {/* Interactive Sacred Geometry Star Archetype Image with Copy Protection & FULLSCREEN MAP Mode */}
          <div 
            onClick={openModal}
            onContextMenu={(e) => e.preventDefault()}
            onDragStart={(e) => e.preventDefault()}
            className="mt-4 overflow-hidden rounded-lg border border-cosmic-indigo/40 shadow-xl relative group cursor-pointer select-none"
            title={language === "en" ? "Click to open Fullscreen Map" : "Klikni za puni zaslon karte"}
          >
            <img 
              src={pleiadesClansArchetypesImg} 
              alt="Pleiades star cluster - harmonic frequency designation" 
              width="1200"
              height="675"
              loading="lazy"
              decoding="async"
              referrerPolicy="no-referrer"
              onContextMenu={(e) => e.preventDefault()}
              onDragStart={(e) => e.preventDefault()}
              className="w-full h-52 sm:h-60 md:h-64 object-cover object-center opacity-90 group-hover:opacity-100 transition-all duration-500 scale-100 group-hover:scale-105 pointer-events-none select-none"
            />

            {/* Hover Indicator — FULLSCREEN MAP button matching StarMap */}
            <div className="absolute inset-0 bg-cosmic-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col items-center justify-center pointer-events-none">
              <div className="bg-quantum-turquoise/10 border border-quantum-turquoise/60 px-4 py-2 rounded-md backdrop-blur-md flex items-center gap-2 text-quantum-turquoise shadow-[0_0_20px_rgba(0,212,255,0.4)] transform translate-y-2 group-hover:translate-y-0 transition-transform duration-300 font-mono text-xs font-bold tracking-wider uppercase">
                <Maximize2 className="w-4 h-4 text-quantum-turquoise animate-pulse" />
                <span>{language === "en" ? "FULLSCREEN MAP" : "PUNI ZASLON KARTA"}</span>
              </div>
            </div>

            {/* HUD border corners for the image */}
            <div className="absolute top-1.5 left-1.5 w-2.5 h-2.5 border-t border-l border-quantum-turquoise/60 pointer-events-none"></div>
            <div className="absolute top-1.5 right-1.5 w-2.5 h-2.5 border-t border-r border-quantum-turquoise/60 pointer-events-none"></div>
            <div className="absolute bottom-1.5 left-1.5 w-2.5 h-2.5 border-b border-l border-quantum-turquoise/60 pointer-events-none"></div>
            <div className="absolute bottom-1.5 right-1.5 w-2.5 h-2.5 border-b border-r border-quantum-turquoise/60 pointer-events-none"></div>
            <div className="absolute inset-0 bg-gradient-to-t from-cosmic-black/50 via-transparent to-transparent pointer-events-none"></div>
          </div>

          <div className="mt-4 pt-3 border-t border-cosmic-indigo/20 flex justify-between items-center text-[10px] font-mono text-gray-500">
            <span>{language === "en" ? "CLAN TYPE:" : "TIP KLANA:"}</span>
            <span className={`${currentArchetype.textColor} uppercase font-bold tracking-widest`}>
              {currentArchetype.id}
            </span>
          </div>
        </div>
      </div>

      {/* FULLSCREEN MAP Protected Mode via createPortal */}
      {typeof document !== "undefined" && createPortal(
        <AnimatePresence>
          {isImageModalOpen && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onContextMenu={(e) => e.preventDefault()}
              className="fixed inset-0 z-[999999] bg-[#040711] p-3 sm:p-5 flex flex-col justify-between w-screen h-screen overflow-hidden select-none"
            >
              {/* Top Fullscreen Cyber HUD Banner matching StarMap */}
              <div className="w-full bg-[#090F20]/90 border border-quantum-turquoise/50 rounded-xl px-4 py-2.5 flex flex-wrap items-center justify-between gap-3 text-xs font-mono text-quantum-turquoise shadow-[0_0_20px_rgba(0,212,255,0.25)] backdrop-blur-md">
                <div className="flex items-center gap-3">
                  <span className="w-2.5 h-2.5 rounded-full bg-quantum-turquoise animate-ping"></span>
                  <div>
                    <span className="font-bold tracking-wider uppercase">
                      {language === "en"
                        ? "IMMERSIVE FULLSCREEN NAVIGATION MODE ACTIVE"
                        : "AKTIVIRAN IMERZIVNI NAVIGACIJSKI MOD PUNA ZASLONA"}
                    </span>
                    <span className="hidden sm:inline-block mx-2 text-gray-500">|</span>
                    <span className="hidden sm:inline-block text-[11px] text-gray-300">
                      {language === "en" 
                        ? "PLEIADES HARMONIC FREQUENCY MAP (M45)" 
                        : "PLEJADSKA KARTA HARMONIJSKIH FREKVENCIJA (M45)"}
                    </span>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  {/* Scan Effect Toggle */}
                  <button
                    onClick={() => setScanEffectActive(!scanEffectActive)}
                    className={`px-3 py-1.5 rounded-lg border text-[11px] font-mono uppercase tracking-wider font-bold transition-all flex items-center gap-1.5 cursor-pointer ${
                      scanEffectActive
                        ? "bg-quantum-turquoise/20 border-quantum-turquoise text-quantum-turquoise shadow-[0_0_12px_rgba(0,212,255,0.35)]"
                        : "bg-gray-900/60 border-gray-700 text-gray-400 hover:text-gray-200"
                    }`}
                    title={language === "en" ? "Toggle Sector Scanning Beam" : "Uključi/Isključi Skeniranje"}
                  >
                    <Radio className={`w-3.5 h-3.5 ${scanEffectActive ? "animate-pulse text-quantum-turquoise" : "text-gray-500"}`} />
                    <span>{scanEffectActive 
                      ? (language === "en" ? "SCAN EFFECT: ON" : "EFEKT SKENIRANJA: UKLJ") 
                      : (language === "en" ? "SCAN EFFECT: OFF" : "EFEKT SKENIRANJA: ISKLJ")}</span>
                  </button>

                  {/* Exit Fullscreen Button */}
                  <button
                    onClick={closeModal}
                    className="px-3.5 py-1.5 rounded-lg bg-amber-500/15 border border-amber-500/50 text-amber-300 hover:bg-amber-500/30 transition-all cursor-pointer flex items-center gap-1.5 font-mono text-xs uppercase tracking-wider font-bold shadow-[0_0_15px_rgba(245,158,11,0.25)]"
                    title={language === "en" ? "Exit Fullscreen Mode (Esc)" : "Izađi iz Puna Zaslona (Esc)"}
                    aria-label={language === "en" ? "Exit Fullscreen Mode" : "Izađi iz Puna Zaslona"}
                  >
                    <Minimize2 className="w-4 h-4 text-amber-400" />
                    <span>{language === "en" ? "EXIT FULLSCREEN (ESC)" : "IZAĐI IZ PUNA ZASLONA (ESC)"}</span>
                  </button>
                </div>
              </div>

              {/* Sub-Header Controls & Navigation Bar */}
              <div className="w-full my-2 bg-cosmic-black/80 border border-quantum-turquoise/30 rounded-xl py-2 px-4 flex flex-wrap items-center justify-between gap-3 text-xs font-mono text-quantum-turquoise">
                <div className="flex items-center gap-2">
                  <Move className="w-4 h-4 animate-pulse text-quantum-turquoise" />
                  <span className="text-[11px] text-gray-300">
                    {language === "en" 
                      ? "Scroll wheel or click map to zoom • Drag mouse to pan around celestial matrix" 
                      : "Skrolaj ili klikni kartu za zumiranje • Povuci mišem za pregled kozmičke matrice"}
                  </span>
                </div>

                <div className="flex items-center gap-3">
                  {/* Zoom Controls Bar */}
                  <div className="flex items-center gap-1 bg-cosmic-indigo/30 border border-quantum-turquoise/40 rounded-lg p-1">
                    <button
                      onClick={handleZoomOut}
                      disabled={zoomScale <= 1}
                      className="p-1.5 rounded bg-cosmic-indigo/40 hover:bg-cosmic-indigo/80 disabled:opacity-40 text-quantum-turquoise transition-colors cursor-pointer"
                      title={language === "en" ? "Zoom out" : "Smanji zum"}
                    >
                      <ZoomOut className="w-4 h-4" />
                    </button>
                    <span className="text-[11px] font-mono font-bold text-quantum-turquoise px-2 min-w-[50px] text-center">
                      {Math.round(zoomScale * 100)}%
                    </span>
                    <button
                      onClick={handleZoomIn}
                      disabled={zoomScale >= 4}
                      className="p-1.5 rounded bg-cosmic-indigo/40 hover:bg-cosmic-indigo/80 disabled:opacity-40 text-quantum-turquoise transition-colors cursor-pointer"
                      title={language === "en" ? "Zoom in" : "Povećaj zum"}
                    >
                      <ZoomIn className="w-4 h-4" />
                    </button>
                    <button
                      onClick={resetZoom}
                      disabled={zoomScale === 1 && panOffset.x === 0 && panOffset.y === 0}
                      className="p-1.5 rounded bg-cosmic-indigo/40 hover:bg-cosmic-indigo/80 disabled:opacity-40 text-gray-300 hover:text-quantum-turquoise transition-colors cursor-pointer ml-1"
                      title={language === "en" ? "Reset zoom" : "Poništi zum"}
                    >
                      <RotateCcw className="w-3.5 h-3.5" />
                    </button>
                  </div>

                  <div className="flex items-center gap-1.5 text-[10px] text-emerald-400 font-bold uppercase tracking-wider bg-emerald-950/40 border border-emerald-500/40 px-2.5 py-1 rounded-lg">
                    <Lock className="w-3.5 h-3.5 text-emerald-400" />
                    <span>{language === "en" ? "COPY PROTECTED" : "ZAŠTIĆENO OD KOPIRANJA"}</span>
                  </div>
                </div>
              </div>

              {/* Main Fullscreen Viewport Container */}
              <div 
                ref={containerRef}
                onWheel={handleWheel}
                onMouseMove={handleMouseMove}
                onMouseDown={handleMouseDown}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseUp}
                onClick={handleImageClick}
                onContextMenu={(e) => e.preventDefault()}
                onDragStart={(e) => e.preventDefault()}
                className={`relative flex-1 w-full bg-cosmic-black rounded-xl border border-quantum-turquoise/50 overflow-hidden shadow-[0_0_50px_rgba(0,212,255,0.25)] select-none ${
                  zoomScale > 1 
                    ? isDragging ? "cursor-grabbing" : "cursor-grab" 
                    : "cursor-zoom-in"
                }`}
              >
                {/* Background image protected with pointer-events-none and overlay layer */}
                <img
                  src={pleiadesClansArchetypesImg}
                  alt="Pleiades star cluster - harmonic frequency designation (High-Res)"
                  onContextMenu={(e) => e.preventDefault()}
                  onDragStart={(e) => e.preventDefault()}
                  style={{
                    transform: `scale(${zoomScale}) translate(${panOffset.x / zoomScale}px, ${panOffset.y / zoomScale}px)`,
                    transformOrigin: `${origin.x}% ${origin.y}%`,
                    transition: isDragging ? "none" : "transform 0.25s cubic-bezier(0.2, 0, 0, 1)"
                  }}
                  className="w-full h-full object-contain select-none pointer-events-none shadow-2xl user-select-none"
                  referrerPolicy="no-referrer"
                />

                {/* Laser Scanning Beam Effect Overlay */}
                {scanEffectActive && (
                  <div className="absolute inset-0 pointer-events-none z-20 overflow-hidden">
                    {/* Vertical Beam Line Sweep */}
                    <motion.div
                      animate={{ y: ["0%", "100%", "0%"] }}
                      transition={{ duration: 4.5, repeat: Infinity, ease: "easeInOut" }}
                      className="absolute left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-cyan-400 to-transparent shadow-[0_0_25px_#00d4ff] z-20"
                    />
                    {/* Beam Glow Tail */}
                    <motion.div
                      animate={{ y: ["-100%", "100%", "-100%"] }}
                      transition={{ duration: 4.5, repeat: Infinity, ease: "easeInOut" }}
                      className="absolute left-0 right-0 h-36 bg-gradient-to-b from-cyan-400/20 via-cyan-500/5 to-transparent z-10"
                    />

                    {/* HUD Radar / Scan Grid Lines */}
                    <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(0,212,255,0.04)_1px,transparent_1px),linear-gradient(to_bottom,rgba(0,212,255,0.04)_1px,transparent_1px)] bg-[size:4rem_4rem] opacity-80"></div>

                    {/* HUD Scan Telemetry Watermark */}
                    <div className="absolute top-4 left-4 bg-cosmic-black/85 border border-quantum-turquoise/50 px-3 py-1.5 rounded-lg text-[10px] font-mono text-quantum-turquoise uppercase tracking-widest backdrop-blur-md flex items-center gap-2 shadow-lg">
                      <span className="w-2 h-2 rounded-full bg-cyan-400 animate-ping"></span>
                      <span>M45 SECTOR SCAN ACTIVE • HARMONIC BAND 432.88 Hz</span>
                    </div>

                    <div className="absolute top-4 right-4 bg-cosmic-black/85 border border-quantum-turquoise/50 px-3 py-1.5 rounded-lg text-[10px] font-mono text-quantum-turquoise uppercase tracking-widest backdrop-blur-md shadow-lg">
                      SIGNAL LOCK: 99.8%
                    </div>

                    {/* Center Targeting Reticle */}
                    <div className="absolute inset-0 flex items-center justify-center opacity-30 pointer-events-none">
                      <div className="w-48 h-48 border border-quantum-turquoise/40 rounded-full animate-spin" style={{ animationDuration: '25s' }}></div>
                      <div className="absolute w-32 h-32 border border-dashed border-quantum-turquoise/60 rounded-full"></div>
                    </div>
                  </div>
                )}

                {/* Transparent Copy Protection Shield Overlay */}
                <div 
                  className="absolute inset-0 z-10 pointer-events-none select-none"
                  onContextMenu={(e) => e.preventDefault()}
                >
                  {/* Subtle watermark */}
                  <div className="absolute bottom-4 right-4 text-[10px] font-mono text-gray-500/50 uppercase tracking-widest font-bold">
                    © PLEIADES GALACTIC ARCHIVE • ALL RIGHTS RESERVED
                  </div>
                </div>

                {/* Corner HUD Frame */}
                <div className="absolute top-3 left-3 w-4 h-4 border-t-2 border-l-2 border-quantum-turquoise pointer-events-none z-30"></div>
                <div className="absolute top-3 right-3 w-4 h-4 border-t-2 border-r-2 border-quantum-turquoise pointer-events-none z-30"></div>
                <div className="absolute bottom-3 left-3 w-4 h-4 border-b-2 border-l-2 border-quantum-turquoise pointer-events-none z-30"></div>
                <div className="absolute bottom-3 right-3 w-4 h-4 border-b-2 border-r-2 border-quantum-turquoise pointer-events-none z-30"></div>
              </div>

              {/* Bottom Fullscreen HUD Footer status */}
              <div className="w-full flex items-center justify-between text-[11px] font-mono text-gray-400 border-t border-cosmic-indigo/40 pt-2.5 mt-2">
                <span className="flex items-center gap-2 text-quantum-turquoise/90">
                  <Lock className="w-3.5 h-3.5 text-emerald-400" />
                  {language === "en" ? "PROTECTED COSMIC ARCHIVE — COPY PROTECTION ACTIVE" : "ZAŠTIĆENI KOZMIČKI ARHIV — AKTIVNA ZAŠTITA OD KOPIRANJA"}
                </span>
                <span className="text-quantum-turquoise font-bold tracking-wider uppercase">
                  {language === "en" ? `FULLSCREEN MODE: ${Math.round(zoomScale * 100)}%` : `PUNI ZASLON: ${Math.round(zoomScale * 100)}%`}
                </span>
              </div>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}
    </div>
  );
}
